import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { useGlobalContext } from 'GlobalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { encryptData } from 'services/authentication/encryptData';

const ThankYouPage: React.FC = () => {
    const { t } = useTranslation();
    const { isTopNavbarEnabled } = useGlobalContext();
    const authContext = useAuthContext();
    const history = useHistory();
    const [pageState, setPageState] = useState<'processing' | 'success' | 'cancelled' | 'finalize_failed'>('processing');
    const [paymentType, setPaymentType] = useState<string | null>(null);
    const [returnTo, setReturnTo] = useState<string | null>(null);

    // Finalize the manual payment and apply the new environment settings
    useEffect(() => {

        const finalize = async () => {

            // Get the payment intent and status from the url
            const url = new URL(window.location.href);
            const paymentIntent = url.searchParams.get("payment_intent");
            const status = url.searchParams.get("redirect_status");

            // Get the optional return path of the menu where the order was started
            const returnToParam = url.searchParams.get("return_to");
            if (returnToParam && returnToParam.startsWith('/') && !returnToParam.startsWith('//')) {
                setReturnTo(returnToParam);
            }

            // Without payment parameters, the payment is collected later by sepa
            if (!paymentIntent && !status) {
                setPageState('success');
                return;
            }

            // Show the cancelled state when the payment was aborted or failed
            if (status !== "succeeded" || !paymentIntent) {
                setPageState('cancelled');
                return;
            }

            // Return if this payment is already finalized
            if (sessionStorage.getItem(`payment_finalized_${paymentIntent}`)) {
                setPageState('success');
                return;
            }

            try {
                // Finalize the manual payment
                const response = await saveData({
                    apiUrl: `finalize_manual_payment/${authContext.environmentHash}`,
                    method: "post",
                    data: {
                        payment_intent: paymentIntent
                    }
                });

                // Show the finalize failed state on an unexpected response
                if (response?.status !== 200) {
                    setPageState('finalize_failed');
                    return;
                }

                // Prevent duplicated finalizing of this payment
                sessionStorage.setItem(`payment_finalized_${paymentIntent}`, "true");

                // Get the environment data and subscription data from the response
                const { environment_data, subscription_data } = response.data;

                // Save the values in the authentication context states
                authContext.setEnvironmentType(environment_data.type);
                authContext.setEnvironmentStatus(environment_data.status);
                authContext.setPackageFeatures(environment_data.package_features);
                authContext.setActiveFeatures(environment_data.active_features);
                authContext.setRoleFeatures(environment_data.role_features);
                authContext.setAccessTo(environment_data.access_to);
                authContext.setAllowedFeatures(environment_data.allowed_features);
                authContext.setAllowedRights(environment_data.allowed_rights);
                authContext.setRedirectTo(environment_data.redirect_to);
                authContext.setSubscriptionIsTrial(subscription_data.is_trial);
                authContext.setSubscriptionStartDate(subscription_data.start_date);
                authContext.setSubscriptionEndDate(subscription_data.end_date);
                authContext.setSubscriptionNoSepaMandate(subscription_data.no_payment_method);
                authContext.setSubscriptionIsProvisional(subscription_data.is_provisional);
                authContext.setPackageIsViewOnly(environment_data.view_only_package === true);

                // Save the values encrypted to the local storage
                localStorage.setItem("type", encryptData(environment_data.type));
                localStorage.setItem("status", encryptData(environment_data.status));
                localStorage.setItem("active_features", encryptData(JSON.stringify(environment_data.active_features)));
                localStorage.setItem("access_to", encryptData(JSON.stringify(environment_data.access_to)));
                localStorage.setItem("package_features", encryptData(JSON.stringify(environment_data.package_features)));
                localStorage.setItem("view_only_package", encryptData(JSON.stringify(environment_data.view_only_package === true)));
                localStorage.setItem("role_features", encryptData(JSON.stringify(environment_data.role_features)));
                localStorage.setItem("allowed_features", encryptData(JSON.stringify(environment_data.allowed_features)));
                localStorage.setItem("allowed_rights", encryptData(JSON.stringify(environment_data.allowed_rights)));
                localStorage.setItem("redirect_to", encryptData(environment_data.redirect_to));
                localStorage.setItem("is_trial", encryptData(subscription_data.is_trial));
                localStorage.setItem("start_date", encryptData(subscription_data.start_date));
                localStorage.setItem("end_date", encryptData(subscription_data.end_date));
                localStorage.setItem("no_payment_method", encryptData(subscription_data.no_payment_method));
                localStorage.setItem("is_provisional", encryptData(subscription_data.is_provisional));

                // Set the payment type to show the right follow up button
                setPaymentType(response.data.payment_type ?? null);

                // Show the success state
                setPageState('success');
            } catch (error) {
                console.error("Finalize failed", error);

                // The payment was received but the activation failed, the webhook will retry it
                setPageState('finalize_failed');
            }
        };

        finalize();
    }, []);

    // Show info when payment is still processing
    if (pageState === 'processing') {
        return (
            <div
                className='container-filled thank-you-page'
                style={{ minHeight: isTopNavbarEnabled ? 'calc(100vh - 187px' : 'calc(100vh - 164px' }}
            >
                <div className='loader' />
                <h2>{t('billing.general.payment_is_processing')}</h2>
                <p>{t('billing.general.payment_is_processing_description')}</p>
            </div>
        );
    }

    // Show thank you on success
    if (pageState === 'success') {
        return (
            <div
                className='container-filled thank-you-page'
                style={{ minHeight: isTopNavbarEnabled ? 'calc(100vh - 187px' : 'calc(100vh - 164px' }}
            >
                <div className='celebrate-icon'>🎉</div>
                <h2>{t('billing.general.thank_you_title')}</h2>
                <p>{t('billing.general.thank_you_description')}</p>
                {returnTo ? (
                    <PrimaryButton
                        label='billing.general.back_to_previous_page_label'
                        size='small'
                        onClick={() => history.push(returnTo)}
                        icon={<FontAwesomeIcon icon={faArrowRight} />}
                    />
                ) : paymentType === 'seats_extension' ? (
                    <PrimaryButton
                        label='billing.general.back_to_users_label'
                        size='small'
                        onClick={() => history.push(`/settings/general/users`)}
                        icon={<FontAwesomeIcon icon={faArrowRight} />}
                    />
                ) : paymentType === 'modules_extension' ? (
                    <PrimaryButton
                        label='billing.general.back_to_modules_label'
                        size='small'
                        onClick={() => history.push(`/settings/features/additional-modules`)}
                        icon={<FontAwesomeIcon icon={faArrowRight} />}
                    />
                ) : (
                    <PrimaryButton
                        label='billing.general.thank_you_get_started_label'
                        size='small'
                        onClick={() => history.push(`/settings/billing/subscription`)}
                        icon={<FontAwesomeIcon icon={faArrowRight} />}
                    />
                )}
            </div>
        )
    }

    // Show the cancelled state when the payment was aborted, the customer can retry from where the order started
    if (pageState === 'cancelled') {
        return (
            <div
                className='container-filled thank-you-page'
                style={{ minHeight: isTopNavbarEnabled ? 'calc(100vh - 187px' : 'calc(100vh - 164px' }}
            >
                <div className='celebrate-icon'>😞</div>
                <h2>{t('billing.general.payment_cancelled_title')}</h2>
                <p>{t(returnTo ? 'billing.general.payment_cancelled_description_return' : 'billing.general.payment_cancelled_description')}</p>
                <PrimaryButton
                    label={returnTo ? 'billing.general.back_to_previous_page_label' : 'billing.general.back_to_wizard_label'}
                    size='small'
                    onClick={() => history.push(returnTo ?? `/settings/billing/subscription/wizard/1`)}
                    icon={<FontAwesomeIcon icon={faArrowRight} />}
                />
            </div>
        );
    }

    // Show that the payment was received but the activation is still pending
    return (
        <div
            className='container-filled thank-you-page'
            style={{ minHeight: isTopNavbarEnabled ? 'calc(100vh - 187px' : 'calc(100vh - 164px' }}
        >
            <div className='celebrate-icon'>⏳</div>
            <h2>{t('billing.general.finalize_failed_title')}</h2>
            <p>{t('billing.general.finalize_failed_description')}</p>
            <PrimaryButton
                label='billing.general.retry_finalize_label'
                size='small'
                onClick={() => window.location.reload()}
                icon={<FontAwesomeIcon icon={faRotateRight} />}
            />
        </div>
    );
};

export default ThankYouPage;
